<template>
  <div class="mt-2">
    <div class="card shadow-sm mb-3">
      <div class="card-body">
        <div class="d-flex flex-wrap gap-3 align-items-end">
          <div>
            <select class="form-select form-select-sm" v-model="filters.promptType" @change="onPromptTypeChange" :disabled="loading" style="width: auto; min-width: 160px;">
              <option value="system">System Prompts</option>
              <option value="suggested">Suggested Prompts</option>
              <option value="organization">Organization Prompts</option>
            </select>
          </div>
          <div v-if="filters.promptType === 'organization'">
            <select class="form-select form-select-sm" v-model="filters.customerId" @change="onCustomerChange" :disabled="loading || loadingCustomers" style="width: auto; min-width: 200px;">
              <option value="">All customers</option>
              <option v-for="customer in customersWithPrompts" :key="customer.customerId" :value="customer.customerId">
                {{ customer.customerName }}
              </option>
            </select>
          </div>
          <div v-if="filters.promptType !== 'organization'">
            <select class="form-select form-select-sm" v-model="filters.featureType" :disabled="loading" style="width: auto; min-width: 140px;">
              <option value="">{{ filters.promptType === 'system' ? 'All Tiers' : 'All Workflows' }}</option>
              <option v-for="feature in featureTypes" :key="feature" :value="feature">{{ formatStartCase(feature) }}</option>
            </select>
          </div>
          <div>
            <select class="form-select form-select-sm" v-model="filters.status" :disabled="loading" style="width: auto; min-width: 120px;" @change="onStatusChange">
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div v-if="filters.promptType === 'system'">
            <select class="form-select form-select-sm" v-model="filters.sortOrder" :disabled="loading" style="width: auto; min-width: 140px;">
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>
          <button class="btn btn-outline-secondary btn-sm" @click="onClearFilters" :disabled="loading">Clear Filters</button>
          <button 
            v-if="filters.promptType === 'organization'" 
            class="btn btn-secondary btn-sm" 
            @click="onCreateNewPrompt"
            :disabled="loading || loadingCustomers"
          >
            Create New Prompt
          </button>
        </div>
      </div>
    </div>

    <div v-if="!loading && (filteredPrompts.length > 0 || (filters.promptType === 'organization' && organizationPromptsResult))" class="px-2 mb-2 text-muted small d-flex align-items-center gap-3 flex-wrap">
      <span v-if="filters.promptType === 'organization'">
        {{ orgPromptsCountLabel }}
      </span>
      <span v-else-if="filteredPrompts.length > 0">
        {{ filteredPrompts.length }} {{ filters.promptType === 'system' ? 'system' : 'suggested' }} prompts found
      </span>
      <div v-if="filters.promptType === 'organization'" class="d-flex align-items-center gap-2">
        <label class="mb-0 small">Per page</label>
        <select class="form-select form-select-sm" v-model.number="orgPromptsPageSize" @change="onOrgPageSizeChange" :disabled="loading" style="width: auto;">
          <option :value="10">10</option>
          <option :value="20">20</option>
          <option :value="50">50</option>
        </select>
      </div>
    </div>

    <div v-if="loading || loadingCustomers || loadingEditPrompt" class="d-flex flex-column align-items-center justify-content-center py-5">
      <div class="spinner-border text-primary mb-2" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <div class="text-primary">{{ loadingEditPrompt ? 'Loading prompt...' : 'Loading prompts...' }}</div>
    </div>

    <div
      v-else-if="
        filteredPrompts.length === 0 &&
        !(filters.promptType === 'organization' && (hasPrevOrganizationPage || hasNextOrganizationPage))
      "
      class="py-3"
    >
      <p class="text-muted" v-if="filters.promptType === 'organization' && !filters.customerId">
        No organization prompts found.
      </p>
      <p class="text-muted" v-else-if="filters.promptType === 'organization' && filters.customerId">
        No organization prompts found for the selected customer.
      </p>
      <p class="text-muted" v-else>
        No prompts match your filter criteria.
      </p>
    </div>

    <div
      v-else
      class="table-responsive table-container scrollable-container prompts-container"
    >
      <table class="table table-hover">
        <thead class="bg-gray-500 text-uppercase sticky-header">
          <tr>
            <th v-if="filters.promptType === 'organization'" style="width: 150px;">Customer</th>
            <th v-else style="width: 300px;">Name</th>
            <th v-if="filters.promptType !== 'organization'" style="width: 150px;">{{ filters.promptType === 'system' ? 'Type' : 'Workflow' }}</th>
            <th 
              v-if="filters.promptType === 'organization'"
              style="width: 100px;" 
              class="sortable-header" 
              @click="toggleSort('version')"
            >
              Version 
              <font-awesome-icon :icon="getSortIcon('version')" :class="getSortIconClass('version')" />
            </th>
            <th v-else style="width: 100px;">Version</th>
            <th
              v-if="filters.promptType === 'organization'"
              style="width: 220px;"
              class="sortable-header"
              @click="toggleSort('createdAt')"
            >
              Created
              <font-awesome-icon :icon="getSortIcon('createdAt')" :class="getSortIconClass('createdAt')" />
            </th>
            <th v-else style="width: 220px;">Created</th>
            <th style="width: 100px;">Status</th>
            <th style="width: 100px;">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="filters.promptType === 'organization' && filteredPrompts.length === 0">
            <td :colspan="7" class="text-muted text-center py-3">No prompts on this page.</td>
          </tr>
          <tr
            v-for="prompt in filteredPrompts"
            :key="prompt.systemPromptId || prompt.suggestedPromptId || prompt.promptId"
          >
            <td v-if="filters.promptType === 'organization'">
              <div class="fw-bold">
                {{ getCustomerName(prompt.customerId) }}
              </div>
            </td>
            <td v-else>
              <div class="fw-bold text-capitalize">
                {{ filters.promptType === 'system' ? formatPromptName(prompt.promptName) : (prompt.promptTitle || 'Untitled') }}
              </div>
            </td>
            <td v-if="filters.promptType !== 'organization'">
              <span v-if="filters.promptType === 'system'" class="badge" :style="{ backgroundColor: getSystemPromptBadgeClass(prompt.promptType), fontSize: '0.75rem' }">
                {{ formatStartCase(prompt.promptType) }}
              </span>
              <span v-else-if="prompt.workflowName" class="badge bg-secondary" style="font-size: 0.75rem;">
                {{ formatStartCase(prompt.workflowName) }}
              </span>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <span v-if="prompt.version">v{{ prompt.version }}</span>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <div class="text-truncate" style="max-width: 200px;">
                {{ formatDate(prompt.createdAt) }}
              </div>
            </td>
            <td>
              <span class="badge" :class="prompt.isActive ? 'bg-success' : 'bg-secondary'">
                {{ prompt.isActive ? 'Active' : 'Inactive' }}
              </span>
            </td>
            <td>
              <div class="d-flex gap-2">
                <button class="btn btn-outline-primary btn-sm" @click="onViewPrompt(prompt)">View</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="filters.promptType === 'organization' && (hasPrevOrganizationPage || hasNextOrganizationPage)" class="px-2 py-2 border-top d-flex align-items-center gap-2">
        <button
          class="btn btn-outline-secondary btn-sm"
          :disabled="loading || !hasPrevOrganizationPage"
          @click="goToPrevOrganizationPage"
        >
          Previous
        </button>
        <button
          class="btn btn-outline-secondary btn-sm"
          :disabled="loading || !hasNextOrganizationPage"
          @click="goToNextOrganizationPage"
        >
          Next
        </button>
      </div>
    </div>
    <PromptViewModal
      v-if="showPromptModal"
      :show="showPromptModal"
      :loading="promptModalLoading"
      :type="promptModalHeader?.type"
      :title="promptModalHeader?.title"
      :featureType="promptModalHeader?.featureType"
      :version="promptModalHeader?.version"
      :order="promptModalHeader?.order"
      :isActive="promptModalHeader?.isActive"
      :createdAt="promptModalHeader?.createdAt"
      :content="promptModalData?.content"
      :description="promptModalData?.description"
      @close="onClosePromptModal"
    />
    <CreateOrganizationPromptModal
      v-if="showCreateModal"
      :show="showCreateModal"
      :draft="createDraft"
      :customers="allCustomersForCreate"
      :customers-with-prompts="customersWithPrompts"
      @close="onCloseCreateModal"
      @review="onCreateReview"
    />
    <ReviewOrganizationPromptModal
      v-if="showReviewModal"
      :show="showReviewModal"
      :draft="createDraft"
      @edit="onReviewEdit"
      @created="onPromptCreated"
    />
    <EditOrganizationPromptModal
      v-if="showEditModal"
      :show="showEditModal"
      :prompt="editingPrompt"
      :return-from-review-draft="draftForEditModal"
      @close="onCloseEditModal"
      @applied-return-draft="onAppliedReturnDraft"
      @review="onEditReview"
      @updated="onPromptUpdated"
      @setActive="onSetActiveFromModal"
      @deactivate="onDeactivateFromModal"
    />
    <RestorePromptConfirmationModal
      v-if="showRestoreModal"
      ref="restoreModal"
      :show="showRestoreModal"
      :prompt="restoringPrompt"
      :currentActiveVersion="currentActiveVersion"
      :customerName="getCustomerName(restoringPrompt?.customerId)"
      :restoring="restoring"
      :isDeactivate="isDeactivateAction"
      @close="onCloseRestoreModal"
      @confirm="onConfirmRestore"
    />
  </div>
</template>

<script>
import PromptViewModal from '../../components/PromptViewModal.vue';
import CreateOrganizationPromptModal from '../../components/CreateOrganizationPromptModal.vue';
import ReviewOrganizationPromptModal from '../../components/ReviewOrganizationPromptModal.vue';
import EditOrganizationPromptModal from '../../components/EditOrganizationPromptModal.vue';
import RestorePromptConfirmationModal from '../../components/RestorePromptConfirmationModal.vue';
import InsightsService from '../../services/InsightsService.js';
import dayjs from 'dayjs';
import { startCase } from 'lodash';

function semverCompare(a, b) {
  // Compare two semantic version strings (e.g., 1.2.10 vs 1.2.2)
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const na = pa[i] || 0;
    const nb = pb[i] || 0;
    if (na > nb) return 1;
    if (na < nb) return -1;
  }
  return 0;
}

export default {
  name: "PromptsManager",
  components: {
    PromptViewModal,
    CreateOrganizationPromptModal,
    ReviewOrganizationPromptModal,
    EditOrganizationPromptModal,
    RestorePromptConfirmationModal,
  },
  data() {
    return {
      model: streetmetrics.model,
      filters: {
        promptType: 'system',
        featureType: '',
        status: '',
        sortOrder: 'desc',
        customerId: '',
      },
      sorting: {
        field: null,
        direction: null, // 'asc' or 'desc'
      },
      // Store both prompt types in state
      systemPrompts: [],
      suggestedPrompts: [],
      organizationPrompts: [],
      organizationPromptsResult: null,
      orgPromptsVisitedPages: [], // { result, data } per page for Previous
      orgPromptsCurrentPageIndex: 0,
      orgPromptsPageSize: 20,
      customersWithPrompts: [],
      loading: false,
      loadingCustomers: false,
      // Modal state
      showPromptModal: false,
      promptModalLoading: false,
      promptModalData: null,
      promptModalHeader: null,
      showCreateModal: false,
      showReviewModal: false,
      createDraft: null,
      draftForEditModal: null,
      reviewSource: 'create',
      showEditModal: false,
      editingPrompt: null,
      loadingEditPrompt: false,
      showRestoreModal: false,
      restoringPrompt: null,
      currentActiveVersion: null,
      restoring: false,
      isDeactivateAction: false,
      promptsFetched: {
        system: false,
        suggested: false,
      },
      insightsService: new InsightsService(),
    };
  },
  computed: {
    featureTypes() {
      const list = this.filters.promptType === 'system' ? this.systemPrompts : this.suggestedPrompts;
      if (this.filters.promptType === 'system') {
        return [...new Set(list.map(p => p.promptType))];
      } else {
        return [...new Set(list.map(p => p.workflowName).filter(Boolean))];
      }
    },
    orgPromptsCountLabel() {
      if (this.filters.promptType !== 'organization') return '';
      const total = this.organizationPromptsResult?.total;
      const count = this.organizationPromptsResult?.count ?? this.organizationPrompts.length;
      if (total != null && count > 0) {
        const start =
          this.orgPromptsVisitedPages
            .slice(0, this.orgPromptsCurrentPageIndex)
            .reduce((sum, p) => sum + (p.result?.count ?? 0), 0) + 1;
        const end = start + count - 1;
        return `Showing ${start}–${end} of ${total} organization prompts`;
      }
      if (total === 0) return '0 organization prompts';
      return `${this.organizationPrompts.length} organization prompt${this.organizationPrompts.length !== 1 ? 's' : ''}`;
    },
    hasNextOrganizationPage() {
      if (this.filters.promptType !== 'organization') return false;
      const c = this.organizationPromptsResult?.nextCursor;
      return c != null && c !== 0;
    },
    hasPrevOrganizationPage() {
      return this.filters.promptType === 'organization' && this.orgPromptsCurrentPageIndex > 0;
    },
    customersWithoutPrompts() {
      if (!this.model.customers || !this.customersWithPrompts) return [];
      const customerIdsWithPrompts = this.customersWithPrompts.map(c => c.customerId);
      return this.model.customers
        .filter(c => !customerIdsWithPrompts.includes(c.customerId))
        .map(c => ({ customerId: c.customerId, customerName: c.customerName }))
        .sort((a, b) => a.customerName.localeCompare(b.customerName));
    },
    allCustomersForCreate() {
      if (!this.model.customers) return [];
      return this.model.customers
        .map(c => ({ customerId: c.customerId, customerName: c.customerName }))
        .sort((a, b) => a.customerName.localeCompare(b.customerName));
    },
    filteredPrompts() {
      if (this.filters.promptType === 'organization') {
        // Server-side filter/sort; table shows current page only
        return this.organizationPrompts || [];
      }
      
      let list = this.filters.promptType === 'system' ? this.systemPrompts : this.suggestedPrompts;      
      if (this.filters.promptType === 'system') {
        if (this.filters.featureType) {
          list = list.filter(p => p.promptType === this.filters.featureType);
        }
      } else {
        if (this.filters.featureType) {
          list = list.filter(p => p.workflowName === this.filters.featureType);
        }
      }
      if (this.filters.status) {
        const isActive = this.filters.status === 'active';
        list = list.filter(p => p.isActive === isActive);
      }
      if (this.filters.promptType === 'system') {
        list = list.slice().sort((a, b) => {
          if (!a.version && !b.version) return 0;
          if (!a.version) return 1;
          if (!b.version) return -1;
          if (this.filters.sortOrder === 'desc') {
            return semverCompare(b.version, a.version);
          } else {
            return semverCompare(a.version, b.version);
          }
        });
      } else {
        list = list.slice().sort((a, b) => {
          const orderA = a.displayOrder || 999;
          const orderB = b.displayOrder || 999;
          return orderA - orderB;
        });
      }
      return list;
    },
  },
  methods: {
    formatStartCase(str) {
      return startCase(str);
    },
    async fetchPrompts(type) {
      this.loading = true;
      try {
        if (type === 'system' && !this.promptsFetched.system) {
          const data = await this.insightsService.getSystemPromptsList();
          this.systemPrompts = data;
          this.promptsFetched.system = true;
        } else if (type === 'suggested' && !this.promptsFetched.suggested) {
          const data = await this.insightsService.getSuggestedPromptsList();
          this.suggestedPrompts = data;
          this.promptsFetched.suggested = true;
        }
      } catch (error) {
        console.error('Error fetching prompts:', error);
      } finally {
        this.loading = false;
      }
    },
    async onPromptTypeChange() {
      this.filters.featureType = '';
      this.filters.status = '';
      this.filters.sortOrder = 'desc';
      this.filters.customerId = '';
      this.organizationPrompts = [];
      this.organizationPromptsResult = null;
      this.orgPromptsVisitedPages = [];
      this.orgPromptsCurrentPageIndex = 0;
      // Reset sorting when switching prompt types
      this.sorting = {
        field: null,
        direction: null,
      };
      
      if (this.filters.promptType === 'organization') {
        await this.loadCustomersWithPrompts();
        await this.fetchOrganizationPrompts();
      } else {
        this.fetchPrompts(this.filters.promptType);
      }
    },
    async loadCustomersWithPrompts() {
      this.loadingCustomers = true;
      try {
        const customerIds = await this.insightsService.getCustomerIdsWithOrganizationPrompts();
        // Match customer IDs with customer names from model.customers
        this.customersWithPrompts = customerIds
          .map(id => {
            const customer = this.model.customers?.find(c => c.customerId === id);
            return customer ? { customerId: id, customerName: customer.customerName } : null;
          })
          .filter(Boolean)
          .sort((a, b) => a.customerName.localeCompare(b.customerName));
      } catch (error) {
        console.error('Error loading customers with prompts:', error);
        this.customersWithPrompts = [];
      } finally {
        this.loadingCustomers = false;
      }
    },
    buildOrgPromptsListParams(cursor = null) {
      const clauses = [];
      if (this.filters.customerId) {
        clauses.push(`customerId.eq:${this.filters.customerId}`);
      }
      if (this.filters.status) {
        clauses.push(`isActive.eq:${this.filters.status === 'active'}`);
      }
      const sortField =
        this.sorting.field === 'version' ? 'version' : this.sorting.field === 'createdAt' ? 'createdAt' : 'createdAt';
      const order = (this.sorting.direction || 'desc').toUpperCase();
      const params = {
        limit: this.orgPromptsPageSize,
        sort: sortField,
        order: order === 'ASC' || order === 'DESC' ? order : 'DESC',
      };
      if (clauses.length) params.search = clauses.join(',');
      if (cursor != null && cursor !== 0) params.cursor = cursor;
      return params;
    },
    async onCustomerChange() {
      await this.fetchOrganizationPrompts();
    },
    async onStatusChange() {
      if (this.filters.promptType === 'organization') {
        await this.fetchOrganizationPrompts();
      }
    },
    async onOrgPageSizeChange() {
      await this.fetchOrganizationPrompts();
    },
    async fetchOrganizationPrompts() {
      this.loading = true;
      this.orgPromptsVisitedPages = [];
      this.orgPromptsCurrentPageIndex = 0;
      try {
        const params = this.buildOrgPromptsListParams();
        const result = await this.insightsService.getOrganizationPromptsList(params);
        const pageData = result.data || [];
        const total = result.total;
        const isOnlyPage = total != null && pageData.length >= total;
        const resultToStore = isOnlyPage ? { ...result, nextCursor: 0 } : result;
        this.organizationPromptsResult = resultToStore;
        this.organizationPrompts = pageData;
        this.orgPromptsVisitedPages = [{ result: { ...resultToStore }, data: [...pageData] }];
      } catch (error) {
        console.error('Error fetching organization prompts:', error);
        this.organizationPrompts = [];
        this.organizationPromptsResult = null;
        this.orgPromptsVisitedPages = [];
      } finally {
        this.loading = false;
      }
    },
    async goToNextOrganizationPage() {
      const nextIndex = this.orgPromptsCurrentPageIndex + 1;
      if (nextIndex < this.orgPromptsVisitedPages.length) {
        this.orgPromptsCurrentPageIndex = nextIndex;
        const page = this.orgPromptsVisitedPages[nextIndex];
        this.organizationPromptsResult = page.result;
        this.organizationPrompts = page.data;
        return;
      }
      const c = this.organizationPromptsResult?.nextCursor;
      if (c == null || c === 0) return;
      this.loading = true;
      try {
        const params = this.buildOrgPromptsListParams(c);
        const result = await this.insightsService.getOrganizationPromptsList(params);
        const data = result.data || [];
        if (data.length === 0) {
          this.organizationPromptsResult = {
            ...this.organizationPromptsResult,
            nextCursor: 0,
          };
          return;
        }
        const total = result.total != null ? result.total : this.organizationPromptsResult?.total;
        const prevSum = this.orgPromptsVisitedPages.reduce(
          (s, p) => s + (p.result?.count ?? p.data?.length ?? 0),
          0
        );
        const isLastPage =
          total != null && (prevSum + data.length >= total);
        const resultWithCursor = isLastPage ? { ...result, total, nextCursor: 0 } : { ...result, total };
        this.orgPromptsVisitedPages.push({
          result: resultWithCursor,
          data,
        });
        this.orgPromptsCurrentPageIndex = this.orgPromptsVisitedPages.length - 1;
        this.organizationPromptsResult = resultWithCursor;
        this.organizationPrompts = data;
      } catch (error) {
        console.error('Error fetching next page:', error);
      } finally {
        this.loading = false;
      }
    },
    goToPrevOrganizationPage() {
      if (this.orgPromptsCurrentPageIndex <= 0) return;
      this.orgPromptsCurrentPageIndex--;
      const page = this.orgPromptsVisitedPages[this.orgPromptsCurrentPageIndex];
      this.organizationPromptsResult = page.result;
      this.organizationPrompts = page.data;
    },
    getCustomerName(customerId) {
      const customer = this.model.customers?.find(c => c.customerId === customerId);
      return customer?.customerName || `Customer ${customerId}`;
    },
    onClearFilters() {
      this.filters.featureType = '';
      this.filters.status = '';
      this.filters.sortOrder = 'desc';
      this.filters.customerId = '';
      if (this.filters.promptType === 'organization') {
        this.fetchOrganizationPrompts();
      }
    },
    formatDate(dateStr) {
      return dayjs(dateStr).format('MMM D, YYYY h:mm A');
    },
    formatPromptName(name) {
      return name.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
    },
    getSystemPromptBadgeClass(promptType) {
      const colors = {
        'base': '#3B82F6',         // Bright blue - foundational
        'context': '#8B5CF6',      // Purple - feature context
        'workflow': '#F59E0B',     // Amber - user actions
        'user': '#14B8A6'          // Teal - user role/communication
      };
      return colors[promptType] || '#6B7280';
    },
    async onViewPrompt(prompt) {
      if (this.filters.promptType === 'system') {
        this.promptModalHeader = {
          type: 'system',
          title: this.formatStartCase(prompt.promptName) + ' System Prompt',
          featureType: this.formatStartCase(prompt.promptType),
          version: prompt.version,
          isActive: prompt.isActive,
          createdAt: this.formatDate(prompt.createdAt),
        };
        this.showPromptModal = true;
        this.promptModalLoading = true;
        this.promptModalData = null;
        
        try {
          const fullPrompt = await this.insightsService.getSystemPromptContent(prompt.systemPromptId);
          this.promptModalData = {
            content: fullPrompt.promptContent,
          };
        } catch (error) {
          console.error('Error fetching prompt content:', error);
          this.promptModalData = {
            content: 'Error loading prompt content. Please try again.',
            description: 'Error loading prompt description. Please try again.',
          };
        } finally {
          this.promptModalLoading = false;
        }
      } else if (this.filters.promptType === 'organization') {
        // For organization prompts, use the EditOrganizationPromptModal in view mode
        this.loadingEditPrompt = true;
        try {
          const history = await this.insightsService.getOrganizationPromptHistory(prompt.customerId);
          const fullPrompt = history.find(p => p.promptId === prompt.promptId);
          
          if (fullPrompt && fullPrompt.promptContent) {
            this.editingPrompt = { ...fullPrompt };
            await this.$nextTick();
            this.loadingEditPrompt = false;
            this.showEditModal = true;
          } else {
            this.loadingEditPrompt = false;
            this.model.toast.message = 'Prompt not found or content unavailable';
            this.model.toast.background = 'bg-danger';
          }
        } catch (error) {
          console.error('Error fetching prompt for view:', error);
          this.loadingEditPrompt = false;
          this.model.toast.message = 'Error loading prompt';
          this.model.toast.background = 'bg-danger';
        }
      } else {
        this.promptModalHeader = {
          type: 'suggested',
          title: prompt.promptTitle || 'Untitled',
          featureType: prompt.workflowName ? this.formatStartCase(prompt.workflowName) : null,
          order: prompt.displayOrder || 1,
          isActive: prompt.isActive,
          createdAt: this.formatDate(prompt.createdAt),
        };
        this.showPromptModal = true;
        this.promptModalLoading = true;
        this.promptModalData = null;
        
        try {
          const fullPrompt = await this.insightsService.getSuggestedPromptContent(prompt.suggestedPromptId);
          this.promptModalData = {
            description: fullPrompt.description,
            content: fullPrompt.promptContent,
          };
        } catch (error) {
          console.error('Error fetching prompt content:', error);
          this.promptModalData = {
            content: 'Error loading prompt content. Please try again.',
            description: 'Error loading prompt description. Please try again.',
          };
        } finally {
          this.promptModalLoading = false;
        }
      }
    },
    onClosePromptModal() {
      this.showPromptModal = false;
      this.promptModalData = null;
      this.promptModalHeader = null;
    },
    onCreateNewPrompt() {
      this.showCreateModal = true;
    },
    onCloseCreateModal() {
      this.showCreateModal = false;
      this.createDraft = null;
    },
    onCreateReview(draft) {
      this.reviewSource = 'create';
      this.createDraft = draft;
      this.showCreateModal = false;
      this.$nextTick(() => {
        this.showReviewModal = true;
      });
    },
    onEditReview(draft) {
      this.reviewSource = 'edit';
      this.createDraft = draft;
      this.showEditModal = false;
      this.$nextTick(() => {
        this.showReviewModal = true;
      });
    },
    onReviewEdit() {
      this.showReviewModal = false;
      this.$nextTick(() => {
        if (this.reviewSource === 'create') {
          this.showCreateModal = true;
        } else {
          this.draftForEditModal = this.createDraft ? { ...this.createDraft } : null;
          this.showEditModal = true;
        }
      });
    },
    onAppliedReturnDraft() {
      this.draftForEditModal = null;
    },
    onSetActiveFromModal(prompt) {
      // This is called from EditOrganizationPromptModal when Set Active is clicked
      // Close the edit modal first, then open restore confirmation
      this.showEditModal = false;
      this.$nextTick(() => {
        this.onRestorePrompt(prompt);
      });
    },
    onDeactivateFromModal(prompt) {
      // This is called from EditOrganizationPromptModal when Deactivate is clicked
      // Close the edit modal first, then open deactivate confirmation
      this.showEditModal = false;
      this.$nextTick(() => {
        this.onDeactivatePrompt(prompt);
      });
    },
    async onDeactivatePrompt(prompt) {
      if (!prompt || !prompt.promptId) return;
      
      // Set up deactivate confirmation modal
      this.restoringPrompt = prompt;
      this.currentActiveVersion = prompt.isActive ? prompt : null;
      this.showRestoreModal = true;
      // Set a flag to indicate this is a deactivate action
      this.isDeactivateAction = true;
    },
    onCloseEditModal() {
      this.showEditModal = false;
      this.editingPrompt = null;
    },
    async onPromptUpdated(customerId) {
      this.showEditModal = false;
      this.editingPrompt = null;
      
      // Refresh the customers with prompts list
      await this.loadCustomersWithPrompts();
      
      // If customerId provided (success), set customer dropdown and fetch prompts for that customer
      if (customerId) {
        // Use $nextTick to ensure Vue has updated the dropdown options
        await this.$nextTick();
        
        // Set the customer filter to show in dropdown
        this.filters.customerId = customerId.toString();
        await this.fetchOrganizationPrompts();
      } else {
        // If error, refresh customers list, clear customer filter and fetch first page of all prompts
        await this.loadCustomersWithPrompts();
        this.filters.customerId = '';
        await this.fetchOrganizationPrompts();
      }
    },
    async onPromptCreated(customerId) {
      this.showCreateModal = false;
      this.showReviewModal = false;
      this.showEditModal = false;
      this.createDraft = null;

      if (customerId) {
        await this.loadCustomersWithPrompts();
        await this.$nextTick();
        this.filters.customerId = customerId.toString();
        await this.fetchOrganizationPrompts();
      } else {
        // If error, refresh customers list, clear customer filter and fetch first page of all prompts
        await this.loadCustomersWithPrompts();
        this.filters.customerId = '';
        await this.fetchOrganizationPrompts();
      }
    },
    compareVersions(a, b) {
      // Use the existing semverCompare function
      return semverCompare(a, b);
    },
    toggleSort(field) {
      if (this.filters.promptType !== 'organization') return;
      if (this.sorting.field === field) {
        if (this.sorting.direction === 'asc') {
          this.sorting.direction = 'desc';
        } else {
          this.sorting.field = null;
          this.sorting.direction = null;
        }
      } else {
        this.sorting.field = field;
        this.sorting.direction = 'asc';
      }
      this.fetchOrganizationPrompts();
    },
    getSortIcon(field) {
      if (this.sorting.field !== field) {
        return 'sort';
      }
      return this.sorting.direction === 'asc' ? 'sort-up' : 'sort-down';
    },
    getSortIconClass(field) {
      if (this.sorting.field !== field) {
        return 'text-muted';
      }
      return 'text-primary';
    },
    async onRestorePrompt(prompt) {
      if (!prompt || !prompt.customerId) return;
      
      // Find the current active version for this customer (if any)
      const customerPrompts = this.organizationPrompts.filter(p => p.customerId === prompt.customerId);
      const activeVersion = customerPrompts.find(p => p.isActive);
      
      // Set up restore modal (set active action)
      this.restoringPrompt = prompt;
      this.currentActiveVersion = activeVersion || null;
      this.isDeactivateAction = false;
      this.showRestoreModal = true;
    },
    onCloseRestoreModal() {
      this.showRestoreModal = false;
      this.restoringPrompt = null;
      this.currentActiveVersion = null;
      this.isDeactivateAction = false;
    },
    async onConfirmRestore(prompt) {
      if (!prompt || !prompt.promptId) return;
      
      // Show loading spinner in restore modal
      this.restoring = true;
      
      try {
        if (this.isDeactivateAction) {
          // Deactivate the prompt
          await this.insightsService.deleteOrganizationPrompt(prompt.promptId);
          
          // Success toast
          this.model.toast.message = `Version ${prompt.version} deactivated successfully`;
          this.model.toast.background = 'bg-success';
        } else {
          // Restore/Set Active the prompt
          await this.insightsService.restoreOrganizationPrompt(prompt.promptId);
          
          // Success toast
          this.model.toast.message = `Version ${prompt.version} set as active successfully`;
          this.model.toast.background = 'bg-success';
        }
        
        // Close restore modal and wait for it to fully close
        if (this.$refs.restoreModal) {
          await this.$refs.restoreModal.closeModal();
        }
        
        // Update state after modal is closed (this stops the modal spinner)
        this.onCloseRestoreModal();
        this.restoring = false;
        
        // Also close view modal if it's open (user might have initiated restore from there)
        if (this.showPromptModal) {
          this.onClosePromptModal();
        }
        
        // Start table spinner, wait 300ms, then fetch
        this.loading = true;
        await new Promise(resolve => setTimeout(resolve, 300));
        
        await this.fetchOrganizationPrompts();
      } catch (error) {
        console.error('Error with prompt action:', error);
        
        // Close modal on error too
        if (this.$refs.restoreModal) {
          await this.$refs.restoreModal.closeModal();
        }
        this.onCloseRestoreModal();
        this.restoring = false;
        
        // Error toast
        const action = this.isDeactivateAction ? 'deactivating' : 'setting version as active';
        const errorMessage = error.response?.data?.message || `Error ${action}. Please try again.`;
        this.model.toast.message = errorMessage;
        this.model.toast.background = 'bg-danger';
      }
    },
  },
  mounted() {
    this.fetchPrompts('system');
  },
};
</script>

<style scoped>
.form-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #555;
  letter-spacing: 0.03em;
}

.prompts-container {
  height: 60vh;
  overflow-y: auto;
}

.sortable-header {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
}

.sortable-header:hover {
  background-color: rgba(255, 255, 255, 0.1);
}
</style> 